import type { FromBasePlayerInput, FromBasePlayerMetadata, FromBaseRow } from './from-base-context';
import { fromBaseUuid } from './from-base-context';
import type { ItemFieldRow } from './item-field-reader';
import { phpJsonInt, phpJsonString } from './php-json-values';

/** Saved choice -> one native source entry. Used at birth and for held edits. */
export function playerChoiceGrant(choice: FromBaseRow, base: Pick<FromBasePlayerInput, 'player_characters' | 'encounter_creatures'>,
    metadata: Pick<FromBasePlayerMetadata, 'additionalAbilityTypes'>, definition: (id: string) => ItemFieldRow | null,
    value: (row: ItemFieldRow | null, key: string) => unknown) {
    const lookup = (id: unknown) => fromBaseUuid(id) ? definition(id) : null;
    const required = (id: unknown) => {
        const row = lookup(id);
        return phpJsonInt(value(row, 'requires_level') ?? value(row, 'requires_class_level') ?? 1);
    };
    const additional = metadata.additionalAbilityTypes.find(row => row.add_ability_name === choice.choice_item_id);
    const layer = additional?.ability_name ?? phpJsonString(choice.choice_type).replace(/^user_choice_\d+_/, '');
    const parentRequiresLevel = Math.max(1, required(choice.choice_item_id), required(choice.exact_choice_item_id));
    const requiresLevel = additional ? 0 : parentRequiresLevel;
    const ability = lookup(choice.choice);
    const entry: FromBaseRow = additional ? {
        source: phpJsonString(value(base.player_characters ?? null, 'item_id') ?? value(base.encounter_creatures ?? null, 'encounter_creature_id')
            ?? value(base as unknown as FromBaseRow, 'item_id')), choice_item_id: additional.add_ability_name,
        exact_choice_item_id: phpJsonString(choice.exact_choice_item_id ?? ''), item_arr: ability,
        built_from: 'add_ability_tab',
    } : {
        source: choice.exact_choice_item_id ?? choice.choice_item_id ?? '',
        choice_item_id: choice.exact_choice_item_id ?? '', exact_choice_item_id: choice.exact_choice_item_id ?? '',
        built_from: 'user_choices', item_arr: ability,
    };
    if (!['at_will_spell', 'innate_spell_per_long_rest'].includes(layer)) entry.requires_level = requiresLevel;
    else if (!additional) delete entry.built_from;
    // These are the prefix's native destinations. A saved edit uses the same
    // construction, then adds/retracts only this choice's contribution.
    const kind = additional ? 'entry' : layer === 'essence_feat' ? 'none'
        : layer === 'damage_type_resistance' ? 'defense'
        : ['damage_type_immune', 'immune_status_effect', 'status_effect_permanent'].includes(layer) ? 'raw' : 'entry';
    const sourceId = phpJsonString(choice.exact_choice_item_id ?? choice.choice_item_id ?? '');
    const defense = { uuid: phpJsonString(value(choice, 'choice') ?? ''), source: {
        source_item_id: sourceId, source_name: fromBaseUuid(sourceId) ? value(definition(sourceId), 'item_name') : null,
        source_kind: 'user_choice',
    } };
    return { layer, entry, requiresLevel, parentRequiresLevel, additional, ability, kind,
        value: kind === 'raw' ? choice.choice ?? null : entry, defense };
}
